import { SettingsIcon } from "../icons";
import { useLongPress } from "../../hooks/useLongPress";

interface TimerAction {
  readonly label: string;
  readonly ariaLabel: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

interface TimerModuleProps {
  readonly isRunning: boolean;
  readonly timeDisplay: React.ReactNode;
  readonly timeColorClass?: string;
  readonly timeSizeClass?: string;
  readonly onStart: () => void;
  readonly onPause: () => void;
  readonly onReset: () => void;
  readonly onSettings: () => void;
  readonly settingsLabel: string;
  readonly info: React.ReactNode;
  readonly extraActions?: ReadonlyArray<TimerAction>;
  readonly footer?: React.ReactNode;
}

export function TimerModule({
  isRunning,
  timeDisplay,
  timeColorClass = "text-accent",
  timeSizeClass = "text-xl md:text-3xl",
  onStart,
  onPause,
  onReset,
  onSettings,
  settingsLabel,
  info,
  extraActions,
  footer,
}: TimerModuleProps) {
  const longPress = useLongPress(onSettings);

  return (
    <div className="relative flex flex-col bg-surface border border-border flex-1 justify-center select-none cursor-default">
      <button
        type="button"
        onClick={onSettings}
        aria-label={settingsLabel}
        className="hidden md:flex absolute top-0 right-0 z-10 w-5 h-5 items-center justify-center text-ink-muted/30 hover:text-ink-muted transition-colors"
      >
        <SettingsIcon className="w-3 h-3" />
      </button>

      <div
        className="flex items-center gap-2 md:gap-3 px-2 md:px-3 py-3"
        title="Maintenir pour configurer"
        {...longPress}
      >
        <div className="flex flex-col gap-0.5 shrink-0">
          {info}
        </div>

        <div className={`${timeSizeClass} font-bold font-mono tabular-nums flex-1 min-w-0 text-center ${timeColorClass}`}>
          {timeDisplay}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={isRunning ? onPause : onStart}
            className="w-14 py-1.5 text-xs font-semibold text-center bg-accent text-white hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-accent rounded"
          >
            {isRunning ? "Pause" : "Start"}
          </button>
          {extraActions?.map((action) => (
            <button
              key={action.ariaLabel}
              type="button"
              onClick={action.onClick}
              disabled={action.disabled}
              aria-label={action.ariaLabel}
              className={action.className ?? "w-7 h-7 flex items-center justify-center text-ink-muted hover:text-ink hover:bg-surface-alt rounded focus:outline-none focus:ring-2 focus:ring-accent"}
            >
              {action.label}
            </button>
          ))}
          <button
            type="button"
            onClick={onReset}
            aria-label="Réinitialiser"
            className="w-7 h-7 flex items-center justify-center text-ink-muted hover:text-ink hover:bg-surface-alt rounded focus:outline-none focus:ring-2 focus:ring-accent"
          >
            ↺
          </button>
        </div>
      </div>

      {footer}
    </div>
  );
}
